import React, { createContext, useState, useContext } from 'react';
import { supabase } from '../supabaseClient';

const SearchContext = createContext();

export const SearchProvider = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const abortControllerRef = React.useRef(null);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    console.log('Searching for:', query);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const abortSignal = abortControllerRef.current.signal;
    try {
      const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
      const activeUser = JSON.parse(localStorage.getItem('gdt_active_user') || '{}');
      const isAdmin = activeUser?.username === 'admin';
      const staffName = activeStaff.full_name || '';

      const cleanQuery = query.replace(/[^0-9a-zA-Z]/g, '');
      const wildcardDni = cleanQuery.length > 0 ? cleanQuery.split('').join('%') : query;

      // 1. Search in Patients (by name or Email or DNI)
      let patientQuery = supabase
        .from('patients')
        .select('id, full_name, dni, email');

      // (We no longer restrict patient search by professional_name, patients are clinic-wide)

      const terms = query.trim().split(/\s+/);
      const andFullName = `and(${terms.map(t => `full_name.ilike.%${t}%`).join(',')})`;
      
      const { data: patientData, error: pError } = await patientQuery
        .or(`${andFullName},dni.ilike.%${query}%,dni.ilike.%${wildcardDni}%,email.ilike.%${query}%`)
        .abortSignal(abortSignal)
        .limit(5);

      if (pError) console.error('Patient search error:', pError);

      const andPatientName = `and(${terms.map(t => `patient_name.ilike.%${t}%`).join(',')})`;

      // 2. Search in Appointments (by DNI, name) - including deep search in JSON
      const searchTerms = [
        `patient_dni.ilike.%${query}%`,
        `patient_dni.ilike.%${wildcardDni}%`,
        andPatientName,
        `study_details->>patient_dni.ilike.%${query}%`,
        `study_details->>patient_dni.ilike.%${wildcardDni}%`,
        `study_details->>patient_firstname.ilike.%${query}%`,
        `study_details->>patient_lastname.ilike.%${query}%`
      ];

      let appointmentQuery = supabase
        .from('appointments')
        .select('id, patient_name, patient_dni, appointment_date, status, study_details');

      if (staffName && !isAdmin) {
        appointmentQuery = appointmentQuery.eq('professional_name', staffName);
      }

      const { data: appointmentData, error: aError } = await appointmentQuery
        .or(searchTerms.join(','))
        .abortSignal(abortSignal)
        .limit(5);
      
      if (aError) console.error('Appointment search error:', aError);

      console.log('Results found:', { patients: patientData?.length, appointments: appointmentData?.length });

      // Combine and format results
      const formattedResults = [
        ...(patientData || []).map(p => ({ 
          id: p.id, 
          title: p.full_name, 
          subtitle: `Paciente - DNI: ${p.dni || 'S/D'}`,
          type: 'patient'
        })),
        ...(appointmentData || []).map(a => ({ 
          id: a.id, 
          title: a.patient_name, 
          subtitle: `Turno: ${a.appointment_date} (${a.patient_dni || a.study_details?.patient_dni || 'Sin DNI'})`,
          type: 'appointment', date: a.appointment_date,
          status: a.status
        }))
      ];

      if (formattedResults.length === 0 && query.length >= 3) {
        setSearchResults([{ id: 'none', title: 'No se encontraron resultados', subtitle: 'Pruebe con otro término', type: 'info' }]);
      } else {
        setSearchResults(formattedResults);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Search aborted for:', query);
        return;
      }
      console.error('Fatal search error:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <SearchContext.Provider value={{ 
      searchQuery, 
      setSearchQuery, 
      handleSearch, 
      searchResults, 
      isSearching, 
      clearSearch,
      handleResultClick: (result, navigate, callback) => {
        clearSearch();
        if (navigate) {
          if (result.type === 'patient') {
            navigate(`/patients/${result.id}`);
          } else if (result.type === 'appointment') {
            navigate(`/calendar?date=${result.date}&appId=${result.id}`);
          }
        }
        if (callback) callback();
      }
    }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
